// @ts-check
import {getISODateString} from '../utils/date-utils';

document.addEventListener('DOMContentLoaded', (e) => {
    const datepicker = document.getElementById('datepicker');

    /**
     * @typedef {Object} Session
     * @prop {number} SessionId
     * @prop {number} SlotMinutes
     * @prop {Array<Slot>} Slots
     */

    /**
     * @typedef {Object} ApiSlotsResponse
     * @prop {number} SiteId
     * @prop {number} ClinicianId
     * @prop {string} Date
     * @prop {Array<Session>} Sessions
     */

    /**
     * @typedef {Object} Slot
     * @prop {boolean} IsAvailable
     * @prop {string} SlotId
     * @prop {string} SlotNote
     * @prop {string} SlotType
     * @prop {string} Time
     */

    /**
     * @typedef {Object} DayWithSlots
     * @prop {number} clinician
     * @prop {Array<Slot>} slots
     */

    /**
     * Represents the state of the booking calendar.
     * @typedef {Object} State
     * @prop {Map<string,Array<DayWithSlots>>} daysWithSlots
     * @prop {boolean} refreshing
     * @prop {Date|null} selectedDate
     * @prop {number|null} selectedClinician
     */

    /**
     * @type {State}
     * */
    const state = {
        daysWithSlots: new Map(),
        refreshing: true,
        selectedDate: null,
        selectedClinician: null,
        selectedClinianHasNoSlots: false,
        visibleYear: null,
        visibleMonth: null,
        yearFetched: null,
        firstMonthFetched: null,
        lastMonthFetched: null,
        cachedSlots: null,
    };

    const hiddenDateField = document.querySelector('input[name="apptDate"]');
    const hiddenClinicianField = document.querySelector(
        'input[name="clinicianId"]',
    );
    const hiddenSlotIdField = document.querySelector('input[name="slotId"]');
    const clinicianButtons = document.querySelectorAll(
        '.book-appt__clinicians .book-appt__clinician-button',
    );
    const tryNextMonthButton = document.querySelector('.try-next-month');

    const calendarSpinner = document.querySelector(
        '.book-appt__clinician-dates-spinner',
    );

    function init() {
        setUpDatePicker();
        setUpClinicianButtons();
    }

    init();

    function setUpClinicianButtons() {
        clinicianButtons.forEach((button) => {
            button.addEventListener('click', () => {
                clinicianButtons.forEach((b) => {
                    b.classList.remove('selected');
                });
                if (state.selectedClinician !== button.value) {
                    button.classList.add('selected');
                    if (parseInt(button.value) === -1) {
                        state.selectedClinician = null;
                    } else {
                        state.selectedClinician = parseInt(button.value);
                    }
                    refreshSlots(true);
                }
            });
        });

        tryNextMonthButton?.addEventListener('click', clickNextMonth);
        if (clinicianButtons.length === 1) {
            state.selectedClinician = parseInt(clinicianButtons[0].value);
            clinicianButtons[0].classList.add('selected');
        }
    }

    function setUpDatePicker() {
        if (!datepicker) return;
        datepicker.quickButtons = [];

        datepicker.addEventListener('componentReady', () => {
            if (!datepicker.startDate) return;
            state.selectedDate = new Date(datepicker.startDate);
            refreshSlots();
            const dateSearch = document.querySelector('#datepicker-input');
            const dateCalendar = document.querySelector(
                '.inclusive-dates-calendar',
            );
            // capture enter key press on form and prevent it from submitting
            dateSearch.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    dateSearch.blur();
                }
            });

            dateSearch.addEventListener('focus', (e) => {
                updateAvailableDates(true);
            });
            // dateSearch.addEventListener('blur', (e) => {
            //   refreshSlots(true);
            // });
        });

        datepicker.addEventListener('selectDate', (e) => {
            if (e.detail) {
                state.selectedDate = new Date(e.detail);
                refreshSlots(true);
            }
        });

        datepicker.addEventListener('changeMonth', (e) => {
            state.visibleYear = parseInt(e.detail.year);
            state.visibleMonth = parseInt(e.detail.month);
            setTimeout(() => {
                refreshSlots(true);
            }, 200);
        });
    }

    /**
     * Disables dates on the datepicker which do not have available slots
     * based on dates and clinicians if one is selected
     * @returns {void}
     */
    function updateAvailableDates(overrideAll = false) {
        datepicker.disableDate = (date) => {
            if (overrideAll) return false;
            const dateToCheck = getISODateString(date);

            if (dateToCheck < getISODateString(new Date())) {
                return true;
            }

            if (state.selectedClinician) {
                if (state.daysWithSlots.has(dateToCheck)) {
                    if (
                        state.daysWithSlots
                            .get(dateToCheck)
                            ?.some(
                                (s) => s.clinician === state.selectedClinician,
                            )
                    ) {
                        return false;
                    }
                    return true;
                }
                return true;
            }
            if (state.daysWithSlots.has(dateToCheck)) {
                return false;
            }
            return true;
        };
    }

    function refreshSlots(updateTimes = false) {
        tryNextMonthButton?.style.setProperty('display', 'none');
        const allClinicianBios = document.querySelectorAll(
            '.book-appt__clinician-times-container',
        );
        allClinicianBios.forEach((bio) => {
            bio.classList.remove('active');
        });
        const errorBoxContainer = document.querySelector(
            `.times-blocks [data-clinician-ccid="${state.selectedClinician}"]`,
        );
        const errorBoxContainers = document.querySelectorAll(
            `.times-blocks [data-clinician-ccid]`,
        );
        [...errorBoxContainers].forEach((el) => {
            el.classList.remove('has-error');
        });

        state.refreshing = true;
        calendarSpinner.classList.add('active');

        getSlots().then((slotsData) => {
            // Save a Set of unique dates which have slotsData
            slotsData.forEach(
                /** @param {ApiSlotsResponse} clinicianDayData */ (
                    clinicianDayData,
                ) => {
                    if (!clinicianDayData.Sessions) return;
                    if (!clinicianDayData.Sessions[0].Slots) return;
                    const clinicianDayDataSlots = Array.from(
                        clinicianDayData.Sessions[0].Slots,
                    );
                    const slots = {
                        clinician: clinicianDayData.ClinicianId,
                        slots: clinicianDayDataSlots,
                    };
                    const date = new Date(clinicianDayData.Date);
                    const formattedDate = getISODateString(date);
                    const existing =
                        state.daysWithSlots.get(formattedDate) ?? [];
                    if (slots.slots.length > 0) {
                        existing.push(slots);
                    }
                    if (existing.length > 0) {
                        state.daysWithSlots.set(formattedDate, existing);
                    }
                },
            );

            let dayData = null;
            dayData = getDayOrFirstDayMatchingClinician();
            if (dayData) {
                datepicker
                    ?.querySelector(`[data-date="${dayData.date}"]`)
                    ?.click();
                state.selectedDate = new Date(dayData.date);
            } else {
                state.selectedDate = null;
                datepicker
                    ?.querySelector('.inclusive-dates-calendar__clear-button')
                    ?.click();
            }

            updateAvailableDates();
            calendarSpinner?.classList.remove('active');
            state.refreshing = false;
            const currentUrl = new URL(window.location.href);
            const others = document.querySelector(
                '.book-appt__clinicians .others',
            );
            const othersWrapper = document.querySelector(
                '.book-appt__clinicians fieldset',
            );
            if (updateTimes) {
                populateTimes(dayData);
                others?.style.setProperty('display', 'none');
                othersWrapper?.classList.remove('has-error');;
            }
            if (state.selectedClinianHasNoSlots) {
                errorBoxContainer?.classList.add('has-error');
                if (currentUrl.searchParams.has('clinician')) {
                    others?.style.setProperty('display', 'block');
                    othersWrapper?.classList.add('has-error');;
                }
            } else if (!state.selectedDate) {
                updateAvailableDates();
                errorBoxContainer?.classList.add('has-error');
                if (currentUrl.searchParams.has('clinician')) {
                    if (state.visibleMonth && state.visibleYear) {
                        const latestDate = new Date();
                        latestDate.setDate(latestDate.getDate() + 56);
                        const latestMonth = latestDate.getMonth() + 1;
                        const latestYear = latestDate.getFullYear();
                        const canMoveToNextMonthWithinEightWeeks =
                            state.visibleYear < latestYear ||
                            (state.visibleYear === latestYear &&
                                state.visibleMonth < latestMonth);

                        if (
                            canMoveToNextMonthWithinEightWeeks &&
                            isNextMonthButtonVisible()
                        ) {
                            tryNextMonthButton?.style.setProperty(
                                'display',
                                'block',
                            );
                        }
                    }
                    others?.style.setProperty('display', 'block');
                    othersWrapper?.classList.add('has-error');;
                }
            }
        });
    }

    async function getSlots() {
        const totalMonthsToFetch = 3;
        let date;
        let month;
        let endMonth;
        let year;
        if (!state.visibleYear || !state.visibleMonth) {
            // let date = state.selectedDate;
            date = new Date();
            // API Accepts dates in YYYY-MM-DD format
            month = ((date.getMonth() % 12) + 1).toString().padStart(2, '0');
            endMonth = (((date.getMonth() + totalMonthsToFetch) % 12) + 1)
                .toString()
                .padStart(2, '0');
            year = date.getFullYear();
            state.visibleYear = parseInt(year);
            state.visibleMonth = parseInt(month);
        }

        if (
            state.yearFetched === state.visibleYear &&
            state.firstMonthFetched <= state.visibleMonth &&
            state.lastMonthFetched >= state.visibleMonth
        ) {
            if (state.cachedSlots) {
                return state.cachedSlots;
            }
        }
        // This flag is only valid for the existing cached slots. There might be more in the next block
        state.selectedClinianHasNoSlots = false;

        if (state.visibleMonth && state.visibleYear) {
            year = state.visibleYear;
            month = state.visibleMonth.toString().padStart(2, '0');
            endMonth = ((state.visibleMonth + totalMonthsToFetch) % 12)
                .toString()
                .padStart(2, '0');
        }

        const endYear = endMonth < month ? year + 1 : year;

        const clinicians = await fetch(
            `/sulis-booking/get-slots/${year}-${month}-01/${endYear}-${endMonth}-07`,
        ).then((response) => response.json());
        state.yearFetched = parseInt(year);
        state.firstMonthFetched = parseInt(month);
        state.lastMonthFetched = parseInt(endMonth);
        state.cachedSlots = clinicians;
        if (clinicians.length === 0) {
            state.selectedClinianHasNoSlots = true;
        }
        return clinicians;
    }

    /**
     *
     * We're finding the first day in the currently visible month on the datepicker.
     * We first check the currently selected date, if it's in the current month, we use that.
     * If not, we use the first day with availbale slots in the currently visible month.
     * If no days are available in the currently visible month, we return null.
     *
     * @returns {DayWithSlots|null}
     *
     */
    function getDayOrFirstDayMatchingClinician() {
        let date = state.selectedDate;
        const visibleMonthYear = datepicker?.querySelector(
            '.inclusive-dates-calendar__calendar',
        )?.attributes['aria-label']?.value;
        const firstDayInVisibleMonthYear = new Date(`1 ${visibleMonthYear}`);

        const selectedDateInCurrentMonthView =
            state.selectedDate?.getMonth() ===
                firstDayInVisibleMonthYear.getMonth() &&
            state.selectedDate?.getFullYear() ===
                firstDayInVisibleMonthYear.getFullYear();

        if (!selectedDateInCurrentMonthView) {
            date = firstDayInVisibleMonthYear;
            state.selectedDate = null;
        }

        const currentSelected = state.daysWithSlots.get(getISODateString(date));
        if (
            currentSelected &&
            currentSelected.some(
                (day) =>
                    day.clinician === state.selectedClinician ||
                    !state.selectedClinician,
            )
        ) {
            return {date, data: currentSelected};
        }

        const entries = state.daysWithSlots.entries();
        let entry = entries.next();
        let found = false;
        while (!entry.done && !found) {
            if (
                entry.value[0].slice(0, 7) !==
                `${state.visibleYear.toString()}-${state.visibleMonth
                    .toString()
                    .padStart(2, '0')}`
            ) {
                entry = entries.next();
                continue;
            }
            if (
                entry.value[1].some(
                    (day) =>
                        day.clinician === state.selectedClinician ||
                        !state.selectedClinician,
                )
            ) {
                found = true;
            } else {
                entry = entries.next();
            }
        }
        if (!found) {
            return null;
        }
        return {date: entry.value[0], data: entry.value[1]};
    }

    function clickNextMonth() {
        const nextMonthButton = datepicker.querySelector(
            '.inclusive-dates-calendar__next-month-button',
        );
        nextMonthButton?.click();
    }

    function isNextMonthButtonVisible() {
        const nextMonthButton = datepicker?.querySelector(
            '.inclusive-dates-calendar__next-month-button',
        );
        if (!nextMonthButton) return false;

        const isAriaDisabled =
            nextMonthButton.getAttribute('aria-disabled') === 'true';

        return !isAriaDisabled;
    }

    function populateTimes(dayData) {
        if (state.daysWithSlots.size === 0) return;
        let clinicianSlots = [];
        if (state.selectedClinician) {
            clinicianSlots = dayData?.data.filter(
                (s) => s.clinician === state.selectedClinician,
            );
        } else {
            clinicianSlots = dayData?.data;
        }

        clinicianSlots?.forEach((clinician) => {
            drawTimesBox(clinician);
        });
    }

    function showError(message) {
        console.log(message);
    }

    function drawTimesBox(clinician) {
        const clinicianBio = document.querySelector(
            `.book-appt__clinician-times-container[data-clinician-ccid="${clinician.clinician}"]`,
        );
        const times = clinicianBio.querySelector(
            '.book-appt__clinician-times-container fieldset',
        );
        const dateLabel = clinicianBio?.querySelector('.selected-date');

        dateLabel.innerText = state.selectedDate.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });

        clinicianBio.classList.add('active');

        const initialConsultationLength = parseInt(
            clinicianBio.dataset.clinicianConsultationLength,
        );

        const slotInterval = initialConsultationLength;

        const {slots} = clinician;
        const filteredSlots = slots;

        const morningSlots = clinicianBio.querySelector(
            '.book-appt__clinician-times-container .morning-slots',
        );
        const afternoonSlots = clinicianBio.querySelector(
            '.book-appt__clinician-times-container .afternoon-slots',
        );
        const morningSlotsEmptyMessage = clinicianBio.querySelector(
            '.book-appt__clinician-times-container .morning-slots-empty',
        );
        const afternoonSlotsEmptyMessage = clinicianBio.querySelector(
            '.book-appt__clinician-times-container .afternoon-slots-empty',
        );

        morningSlotsEmptyMessage?.classList.remove('active');
        afternoonSlotsEmptyMessage?.classList.remove('active');

        [...morningSlots.children, ...afternoonSlots.children].forEach((el) => {
            el.remove();
        });

        filteredSlots.forEach((slot) => {
            const timeInput = createTimeInput(slot);
            if (!timeInput) return;
            if (parseInt(slot.Time) < 12) {
                morningSlots.appendChild(timeInput);
            } else {
                afternoonSlots.appendChild(timeInput);
            }
        });
        if (morningSlots.children.length === 0) {
            morningSlotsEmptyMessage?.classList.add('active');
        }
        if (afternoonSlots.children.length === 0) {
            afternoonSlotsEmptyMessage?.classList.add('active');
        }
    }

    function createTimeInput(slot) {
        if (!slot.Time) return null;
        const timeInputWrapper = document.createElement('div');
        const timeInputEl = document.createElement('input');
        timeInputWrapper.classList.add('time-input-wrapper');
        timeInputEl.type = 'radio';
        timeInputEl.name = 'apptTime';
        timeInputEl.value = slot.SlotId;
        timeInputEl.id = `slot-${slot.SlotId}`;
        timeInputEl.classList.add('sr-only');
        const labelEl = document.createElement('label');
        labelEl.htmlFor = `slot-${slot.SlotId}`;
        labelEl.innerText = slot.Time;

        timeInputEl.addEventListener('change', () => {
            hiddenDateField.value = state.selectedDate.toISOString();
            let clinician = state.selectedClinician;
            if (!clinician) {
                clinician = timeInputEl.closest(
                    '.book-appt__clinician-times-container',
                ).dataset.clinicianCcid;
            }
            hiddenClinicianField.value = parseInt(clinician);
            hiddenSlotIdField.value = slot.SlotId;
            console.log(clinician);
        });

        timeInputWrapper.appendChild(timeInputEl);
        timeInputWrapper.appendChild(labelEl);

        return timeInputWrapper;
    }
});
